import React, { useEffect, useState, useRef } from 'react'
import { Routes, Route, useLocation, useParams, useNavigate, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { createClientComponentClient } from '@/integrations/supabase/client'
import { getDashboardPath } from '@/utils/getDashboardPath'
import I18nProvider from './components/providers/I18nProvider'
import AccessibilityProvider from './components/providers/AccessibilityProvider'
import { AuthProvider } from '@/contexts/AuthContext'
import { useAuth } from '@/hooks/useAuth'
import { useAdmin } from '@/hooks/useAdmin'
import Header from './components/layout/Header'
import Footer from './components/layout/Footer'
import ScrollManager from './components/layout/ScrollManager'
import MemoryPreloaderProvider from './components/providers/MemoryPreloaderProvider'
import { SubdomainBrandingProvider, useFacilityBranding } from './contexts/SubdomainBrandingContext'
import { SupportWidget } from './components/support/SupportWidget'
import CredentialPage from './pages/CredentialPage'
import VerificationPage from './pages/VerificationPage'
import UniversityBrandAdminPage from './pages/UniversityBrandAdminPage'
import WhitelabelLandingPage from './pages/WhitelabelLandingPage'
import TransferPage from './pages/TransferPage'
import { RootPortalRouter } from './pages/BrandedPortalPage'

// ── Core pages ────────────────────────────────────────────────────────────────
import HomePage from './pages/HomePage'
import HowItWorksPage from './pages/HowItWorksPage'

import ProfilePage from './pages/ProfilePage'
import SettingsPage from './pages/SettingsPage'
import LoginPage from './pages/LoginPage'
import SignupPage from './pages/SignupPage'
import CategoryPage from './app/category/[slug]/page'
import AdminPage from './pages/AdminPage'
import ConversationsPage from './app/conversations/page'
import ConversationPage from './app/conversation/[id]/page'
import OnboardingPage from './pages/OnboardingPage'
import SubscriptionPage from './pages/SubscriptionPage'
import PrivacyPage from './pages/PrivacyPage'
import TermsPage from './pages/TermsPage'
import PrivacySecurityPage from './pages/PrivacySecurityPage'
import HelpPage from './pages/HelpPage'
import VerifyPage from './pages/VerifyPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import FacilitySignupPage from './pages/FacilitySignupPage'
import FacilityAdminPage from './pages/FacilityAdminPage'
import FamilySignupPage from './pages/FamilySignupPage'
import FamilyAdminPage from './pages/FamilyAdminPage'
import EducationSignupPage from './pages/EducationSignupPage'
import EducationAdminPage from './pages/EducationAdminPage'
import StudentProgressPage from './pages/StudentProgressPage'
import SoloSubjectsPage from './pages/learn/SoloSubjectsPage'
import EducationSessionPage from './pages/EducationSessionPage'
import LearningRecordPage from './pages/records/LearningRecordPage'
import TranscriptPage from './pages/records/TranscriptPage'
import CurriculumOutlinePage from './pages/records/CurriculumOutlinePage'
import PWAUpdateHelper from './components/pwa/PWAUpdateHelper'

// ── Homeschool pages ──────────────────────────────────────────────────────────
import HomeschoolLandingPage from './pages/homeschool/Homeschoollandingpage'
import HomeschoolEnrolPage from './pages/homeschool/Homeschoolenrolpage'
import ParentDashboardPage from './pages/homeschool/Parentdashboardpage'
import ChildProgressPage from './pages/homeschool/Childprogresspage'
import ChildSubjectsPage from './pages/homeschool/Childsubjectspage'
import HomeschoolSubscriptionPage from './pages/homeschool/HomeschoolSubscriptionPage'

// ── Elite Academy pages ───────────────────────────────────────────────────────
import EliteAcademyLandingPage from './pages/elite/EliteAcademyLandingPage'

// ── Prep (Free Foundation Subjects) ──────────────────────────────────────────
import PrepPage from './pages/prep/PrepPage'

// ── Sponsorship pages ─────────────────────────────────────────────────────────
import SponsorLandingPage from './pages/SponsorLandingPage'
import SponsorHub from './pages/SponsorHub'
import SponsorSuccessPage from './pages/SponsorSuccessPage'
import RedeemSponsorPage from './pages/RedeemSponsorPage'
import SponsorCheckout from './pages/SponsorCheckout'
import SponsorDashboard from './pages/SponsorDashboard'
import SponsorChildDetailPage from './pages/SponsorChildDetailPage'
import SponsorCatalogPage from './pages/sponsor/SponsorCatalogPage'

// ── Admin pages ───────────────────────────────────────────────────────────────
import SponsorshipAdminPage from './pages/admin/SponsorshipAdminPage'

// ── NGO / NFP Partner pages ───────────────────────────────────────────────────
import NgoLandingPage from './pages/NgoLandingPage'
import NgoDashboardPage from './pages/NgoDashboardPage'
import NgoApplyPage from './pages/NgoApplyPage'

// ── Affiliate pages ───────────────────────────────────────────────────────────
import AffiliateLandingPage from './pages/affiliates/AffiliateLandingPage'
import AffiliateApplyPage from './pages/affiliates/AffiliateApplyPage'
import AffiliateDashboardPage from './pages/affiliates/AffiliateDashboardPage'

// ── Schools pages ────────────────────────────────────────────────────────────
import SchoolsLandingPage from './pages/schools/SchoolsLandingPage'

// ── Church pages ──────────────────────────────────────────────────────────────
import ChurchLandingPage from './pages/church/Churchlandingpage'
import ChurchRegisterPage from './pages/church/Churchregisterpage'
import ChurchAdminPage from './pages/church/Churchadminpage'
import ChurchMemberPage from './pages/church/Churchmemberpage'
import ChurchMemberDetailPage from './pages/church/ChurchMemberDetailPage'
import ChurchSessionPage from './pages/church/ChurchSessionPage'

// ── Still Waters University pages (Live Native Suite) ─────────────────────────
import UniversityLandingPage from '@/pages/StillWaters_University/Universitylandingpage'
import UniversityEnrolPage from '@/pages/StillWaters_University/Universityenrolpage'
import UniversityDashboardPage from '@/pages/StillWaters_University/Universitydashboardpage'
import UniversityDegreePage from '@/pages/StillWaters_University/Universitydegreepage'
import UniversityModulePage from '@/pages/StillWaters_University/Universitymodulepage'
import UniversitySessionPage from '@/pages/StillWaters_University/Universitysessionpage'
import UniversityProgressPage from '@/pages/StillWaters_University/Universityprogresspage'
import UniversityCertificatesPage from '@/pages/StillWaters_University/Universitycertificatespage'
import UniversityCorporatePage from '@/pages/StillWaters_University/Universitycorporatepage'
import UniversityCorporateAdminPage from '@/pages/StillWaters_University/Universitycorporateadminpage'
import UniversityNgoDashboardPage from '@/pages/StillWaters_University/Universityngodashboardpage'
import UniversityNfpPage from '@/pages/StillWaters_University/Universitynfppage'
import UniversityGiftPage from '@/pages/StillWaters_University/Universitygiftpage'
import UniversityGivePage from '@/pages/StillWaters_University/Universitygivepage'
import UniversityRedeemPage from '@/pages/StillWaters_University/Universityredeempage'
import UniversityActivatePage from '@/pages/StillWaters_University/Universityactivatepage'

import { usePositionMemory } from '@/hooks/usePositionMemory'
import PasswordSetupGate from '@/components/auth/PasswordSetupGate'

// ─────────────────────────────────────────────────────────────────────────────
// Language handler
// ─────────────────────────────────────────────────────────────────────────────
function LanguageRouteHandler() {
  const { lang } = useParams<{ lang: string }>()
  const { i18n } = useTranslation()
  useEffect(() => {
    if (lang && ['en', 'es', 'fr', 'sv', 'de', 'id', 'zh-CN'].includes(lang)) {
      i18n.changeLanguage(lang)
    }
  }, [lang, i18n])
  return null
}

function DashboardRedirect() {
  const { user, userProfile } = useAuth()
  const { isAdmin } = useAdmin()
  const dest = user ? (isAdmin ? '/admin' : getDashboardPath(userProfile, user?.user_metadata)) : '/'
  return <Navigate to={dest} replace />
}

// ─────────────────────────────────────────────────────────────────────────────
// Auth redirect guard
// ─────────────────────────────────────────────────────────────────────────────
function AuthRedirectGuard() {
  const { user, userProfile, isLoading: authLoading } = useAuth()
  const { isAdmin, loading: adminLoading } = useAdmin()
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    if (authLoading || adminLoading || !user || !userProfile) return

    // /auth/verify self-routes: VerifyPage mirrors this guard's priority chain
    // (return-path, faith intent, NGO link, getDashboardPath). Firing here too
    // races its delayed navigate — the guard consumes the stashed intent, then
    // the verify page's 1500ms navigate rips the user off their destination.
    const isAuthPath =
      (location.pathname.startsWith('/auth') && !location.pathname.startsWith('/auth/verify')) ||
      location.pathname === '/signin' ||
      location.pathname === '/signup' ||
      location.pathname === '/'
    if (!isAuthPath) return

    const determineRedirect = async () => {
      try {
        if (isAdmin) { navigate('/admin', { replace: true }); return }
        // Pure affiliate user (no facility, no special role)
        if (
          userProfile.is_affiliate &&
          userProfile.affiliate_status === 'approved' &&
          !userProfile.facility_id &&
          !userProfile.facility_role
        ) {
          navigate('/affiliates/dashboard', { replace: true }); return
        }

        // Pending affiliate — route to dashboard which shows pending state
        if (
          userProfile.affiliate_status === 'pending' &&
          !userProfile.facility_id
        ) {
          navigate('/affiliates/dashboard', { replace: true }); return
        }

        // Check for return path after OAuth (e.g. /homeschool/enrol?plan=edu_elite&type=individual)
        try {
          const returnPath = localStorage.getItem('sage_auth_return_path')
          if (returnPath) {
            localStorage.removeItem('sage_auth_return_path')
            const safe = returnPath.startsWith('/') && !returnPath.startsWith('//')
            // Also reject the legacy auth aliases — a stale "/signin" here
            // loops a signed-in user back to login (the Stripe-bounce flicker).
            const isAuthPage = returnPath.startsWith('/auth') || returnPath === '/signin' || returnPath === '/signup'
            if (safe && returnPath !== '/' && returnPath !== '/learn' && !isAuthPage) {
              console.log('[AUTH] Return path detected, redirecting to', returnPath)
              navigate(returnPath, { replace: true })
              return
            }
          }
        } catch { }

        // Check for Faith / Bible Journey intent (mirroring sage_prep_intent)
        try {
          const faithIntent = localStorage.getItem('sage_faith_intent') || sessionStorage.getItem('sage_faith_intent')
          if (faithIntent) {
            localStorage.removeItem('sage_faith_intent')
            sessionStorage.removeItem('sage_faith_intent')
            if (faithIntent.startsWith('/church/')) {
              console.log('[AUTH] Faith intent detected, redirecting to', faithIntent)
              navigate(faithIntent, { replace: true })
              return
            }
          }
        } catch { }

        const supabase = createClientComponentClient()

        const role = userProfile?.facility_role
        const facilityId = userProfile?.facility_id

        // ── NGO contact redirect: only check if user isn't already in a hub ──
        // email match covers the pre-provision sign-in window; contact_user_id is
        // set server-side by invite-ngo at link generation.
        // Skip for users who already have a facility_id — they are hub members, not NGO contacts.
        if (!facilityId) {
          const { data: ngoOrg } = await supabase
            .from('org_nominations')
            .select('id')
            .or(`contact_user_id.eq.${user.id},org_contact_email.eq.${user.email}`)
            .limit(1)
            .maybeSingle()
          if (ngoOrg) { navigate('/ngo/dashboard', { replace: true }); return }
        }

        let facType: string | null = null
        if (facilityId) {
          const { data: facility } = await supabase
            .from('facilities')
            .select('type, facility_type')
            .eq('id', facilityId)
            .single()
          facType = facility?.facility_type || facility?.type || null
        }

        // ── University student redirect (Section 6.2) ────────────────────
        if (role === 'university_student') { navigate('/university/dashboard', { replace: true }); return }

        // Check for active university enrolments (hybrid model)
        const { count: uniCount } = await (supabase as any)
          .from('university_enrolments')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('status', 'active')
        if ((uniCount ?? 0) > 0) { navigate('/university/dashboard', { replace: true }); return }

        if (role === 'teacher' && facilityId) { navigate('/education-admin', { replace: true }); return }
        if (role === 'student') { navigate('/learn', { replace: true }); return }

        if (role === 'admin' && facilityId) {
          // ── HARD GATE: platform orgs are their own kind. facility_type 'platform'
          // is disjoint from every legacy value — the NGO portal, church admin, and
          // school surfaces never see them. org flavor rides branding.org_type.
          if (facType === 'platform') {
            navigate('/platform/setup', { replace: true }); return
          }
          if (facType === 'church') { navigate('/church/admin', { replace: true }); return }
          if (facType === 'company' || facType === 'corporate') { navigate('/university/corporate/admin', { replace: true }); return }
          if (facType === 'university_ngo') { navigate('/university/ngo/dashboard', { replace: true }); return }
          if (facType === 'ngo')            { navigate('/ngo/dashboard', { replace: true }); return }
          // Homeschool AND family_edu both go to the parent hub dashboard
          if (facType === 'homeschool' || facType === 'family_edu') { navigate('/homeschool/parent', { replace: true }); return }
          if (facType === 'school' || user?.user_metadata?.is_education_admin === true) {
            navigate('/education-admin', { replace: true }); return
          }
          if (facType === 'family' || user?.user_metadata?.is_family_admin === true) {
            navigate('/family-admin', { replace: true }); return
          }
          navigate('/facility-admin', { replace: true }); return
        }

        if (role === 'member' && facilityId) {
          // ── HARD GATE (member side): platform learners → /learn
          if (facType === 'platform') {
            navigate('/learn', { replace: true }); return
          }
          if (facType === 'church') { navigate('/church/learn', { replace: true }); return }
          if (facType === 'university_ngo') { navigate('/university/dashboard', { replace: true }); return }
          if (facType === 'company' || facType === 'corporate') { navigate('/university/dashboard', { replace: true }); return }
          if (facType === 'ngo') { navigate('/ngo/dashboard', { replace: true }); return }
          // Homeschool, family_edu, and school child members go to the /learn student dashboard
          if (facType === 'homeschool' || facType === 'family_edu' || facType === 'school') {
            navigate('/learn', { replace: true }); return
          }
        }

        if (user?.user_metadata?.is_education_admin === true) { navigate('/education-admin', { replace: true }); return }
        if (user?.user_metadata?.is_facility_admin === true || user?.user_metadata?.is_family_admin === true) {
          navigate(user?.user_metadata?.is_family_admin ? '/family-admin' : '/facility-admin', { replace: true }); return
        }

        // Check for Prep intent
        try {
          const prepIntent = localStorage.getItem('sage_prep_intent') === 'true'
          if (prepIntent) {
            localStorage.removeItem('sage_prep_intent')
            console.log('[AUTH] Prep intent detected, redirecting to /learn')
            navigate('/learn', { replace: true }); return
          }
        } catch { }

        // ── Paid solo learners (no facility, no role) ─────────────────────
        const soloPlan =
          (userProfile?.preferences as any)?.billing?.plan || userProfile?.active_plan || ''

        // Solo university learner (university_access or university plan)
        if (!facilityId && !role && (
          userProfile?.university_access ||
          soloPlan.startsWith('university_') ||
          soloPlan.startsWith('scholar_pass_')
        )) {
          navigate('/university/dashboard', { replace: true }); return
        }

        // Solo academy learner
        if (!facilityId && !role && ['homeschool_single', 'edu_elite', 'edu_bundle'].includes(soloPlan)) {
          navigate('/learn', { replace: true }); return
        }

        // ── Check for Saved Position (Position Memory) ──
        // This takes highest priority for a "native app" feel
        try {
          const savedPos = localStorage.getItem('sage_last_position')
          const savedTime = localStorage.getItem('sage_last_position_timestamp')
          const isRestored = sessionStorage.getItem('sage_position_restored_this_session') === 'true'

          if (savedPos && savedTime && !isRestored) {
            const age = Date.now() - parseInt(savedTime, 10)
            const MAX_AGE = 7 * 24 * 60 * 60 * 1000 // 7 days (matches usePositionMemory)

            if (age < MAX_AGE && savedPos !== location.pathname) {
              console.log('[AUTH] Position memory detected, deferring to restoration logic:', savedPos)
              // We'll let the usePositionMemory hook handle the actual navigation
              // to keep the logic unified, but we prevent AuthRedirectGuard from overriding.
              return
            }
          }
        } catch { }

        // Companion home only for RECENT activity (30d). Legacy users with stale
        // chat history route to education — v2.6 count rule superseded 2026-10-06.
        const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
        const { count: recentCount, error } = await supabase
          .from('conversations')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .gte('created_at', thirtyDaysAgo)
        if (error) throw error
        navigate((recentCount ?? 0) > 0 ? '/learn' : '/learn', { replace: true })
      } catch {
        navigate('/learn', { replace: true })
      }
    }

    determineRedirect()
  }, [user, userProfile, authLoading, adminLoading, isAdmin, location.pathname, navigate])

  return null
}

// ─────────────────────────────────────────────────────────────────────────────
// Brand & Domain Configuration
// If VITE_BRAND is 'academy' or running on stillwatersacademy domain,
// the homepage is EliteAcademyLandingPage.
// ─────────────────────────────────────────────────────────────────────────────
const IS_ACADEMY = (import.meta as any).env?.VITE_BRAND === 'academy' ||
  (typeof window !== 'undefined' && (
    window.location.hostname.includes('stillwaters') ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    !window.location.hostname.includes('companion')
  ))

// /elite always renders the Elite landing page — on every domain. The old
// IS_ACADEMY gate recycled the route back to the homepage on stillwatersacademy
// (and localhost), making the doors' Elite card a dead loop. IS_ACADEMY remains
// the homepage-selector only; routes must never gate on it.
const EliteRoute = () => <EliteAcademyLandingPage />

// ─────────────────────────────────────────────────────────────────────────────
// Route list — pages rendered inside the shared Header/Footer shell.
// University routes are NOT here — they manage their own layout entirely.
// ─────────────────────────────────────────────────────────────────────────────
const routeList = [
  // Auth & core
  // Root: org subdomains → branded front door; main SWA domain → HomePage
  { path: '', Component: RootPortalRouter },
  { path: 'how-it-works', Component: HowItWorksPage },
  { path: 'onboarding', Component: OnboardingPage },
  { path: 'subscription', Component: SubscriptionPage },
  { path: 'profile', Component: ProfilePage },
  { path: 'settings', Component: SettingsPage },
  { path: 'auth/login', Component: LoginPage },
  { path: 'auth/signup', Component: SignupPage },
  { path: 'auth/verify', Component: VerifyPage },
  { path: 'auth/reset-password', Component: ResetPasswordPage },
  { path: 'signin', Component: LoginPage },
  { path: 'signup', Component: SignupPage },
  { path: 'privacy', Component: PrivacyPage },
  { path: 'terms', Component: TermsPage },
  { path: 'security', Component: PrivacySecurityPage },
  { path: 'help', Component: HelpPage },
  { path: 'contact', Component: HelpPage },

  // Conversations
  { path: 'category/:slug', Component: CategoryPage },
  { path: 'conversations', Component: ConversationsPage },
  { path: 'conversation/:id', Component: ConversationPage },

  // Admin
  { path: 'admin', Component: AdminPage },

  // Facility / family
  { path: 'facility-signup', Component: FacilitySignupPage },
  { path: 'family-signup', Component: FamilySignupPage },
  { path: 'facility-admin', Component: FacilityAdminPage },
  { path: 'family-admin', Component: FamilyAdminPage },

  // Education hub
  { path: 'education-signup', Component: EducationSignupPage },
  { path: 'education-admin', Component: EducationAdminPage },

  // Credential verification (public — no auth required)
  { path: 'credentials/:slug', Component: CredentialPage },
  { path: 'verification', Component: VerificationPage },

  // Cross-facility transfer consent (public with signed token)
  { path: 'transfer', Component: TransferPage },

  // The platform — public journey: /platform · setup: /platform/setup
  { path: 'platform', Component: WhitelabelLandingPage },
  { path: 'platform/setup', Component: UniversityBrandAdminPage },
  // Legacy aliases — old links keep working
  { path: 'whitelabel', Component: WhitelabelLandingPage },
  { path: 'university-brand/admin', Component: UniversityBrandAdminPage },
  { path: 'learn', Component: StudentProgressPage },
  { path: 'learn/subjects', Component: SoloSubjectsPage },
  { path: 'learn/session', Component: EducationSessionPage },

  // Learning records / transcripts / curriculum outlines
  { path: 'record/me', Component: TranscriptPage },
  { path: 'record/:childId', Component: TranscriptPage },
  { path: 'record/:childId/:classId', Component: LearningRecordPage },
  { path: 'outline/:classId', Component: CurriculumOutlinePage },
  { path: 'curriculum/:classId', Component: CurriculumOutlinePage },

  // Homeschool
  { path: 'homeschool', Component: HomeschoolLandingPage },
  { path: 'homeschool/enrol', Component: HomeschoolEnrolPage },
  { path: 'homeschool/enrol-child', Component: HomeschoolEnrolPage },
  { path: 'homeschool/parent', Component: ParentDashboardPage },
  { path: 'homeschool/child/:childId', Component: ChildProgressPage },
  { path: 'homeschool/child/:childId/subjects', Component: ChildSubjectsPage },
  { path: 'homeschool/subscription', Component: HomeschoolSubscriptionPage },

  // Elite Academy — short URL: stillwatersacademy.com/elite
  { path: 'elite', Component: EliteRoute },
  { path: 'elite-academy', Component: EliteRoute }, // legacy redirect

  // Prep — 4 free foundation subjects
  { path: 'prep', Component: PrepPage },

  // Sponsorship
  { path: 'sponsor', Component: SponsorLandingPage },            
{ path: 'sponsor/give', Component: SponsorHub },        
  { path: 'sponsor/sent', Component: SponsorSuccessPage },
  { path: 'redeem-sponsor', Component: RedeemSponsorPage },
  { path: 'redeem-sponsor/:code', Component: RedeemSponsorPage },
  { path: 'sponsor/corporate', Component: SponsorCheckout },
  { path: 'sponsor/dashboard', Component: SponsorDashboard },
  { path: 'sponsor/child/:userId', Component: SponsorChildDetailPage },
    { path: 'sponsor/catalog', Component: SponsorCatalogPage },

  // Admin
  { path: 'admin/sponsorship', Component: SponsorshipAdminPage },

  // NGO / NFP Partner Portal
  { path: 'ngo', Component: NgoLandingPage },
  { path: 'ngo/dashboard', Component: NgoDashboardPage },
  { path: 'ngo/apply', Component: NgoApplyPage },

  // Schools
  { path: 'schools', Component: SchoolsLandingPage },

  // Church
  { path: 'church', Component: ChurchLandingPage },
  { path: 'church/register', Component: ChurchRegisterPage },
  { path: 'church/admin', Component: ChurchAdminPage },
  { path: 'church/learn', Component: ChurchMemberPage },
  { path: 'church/member/:memberId', Component: ChurchMemberDetailPage },

  // Affiliates
  { path: 'affiliates', Component: AffiliateLandingPage },
  { path: 'affiliates/apply', Component: AffiliateApplyPage },
  { path: 'affiliates/dashboard', Component: AffiliateDashboardPage },
]

// ─────────────────────────────────────────────────────────────────────────────
// Shell Layout Prefixes:
// - CHAT_PREFIXES: Fullscreen interactive sessions (100dvh lock, internal flex/scroll, no Header/Footer)
// - PRINT_PREFIXES: Print artifacts (Learning Record, Transcript, Outlines — natural scroll, no Header/Footer)
// ─────────────────────────────────────────────────────────────────────────────
const CHAT_PREFIXES = [
  '/conversation/',
  '/category/',
  '/learn/session',
  '/church/session',
  '/university/learn',
  '/university/degree',
  '/university/module',
]

const PRINT_PREFIXES = [
  '/record',
  '/outline',
  '/curriculum',
]


// ─────────────────────────────────────────────────────────────────────────────
// AppShell
// ─────────────────────────────────────────────────────────────────────────────
function AppShell() {
  const location = useLocation()

  useEffect(() => {
    document.documentElement.classList.add('dark')
  }, [])
  const normalizedPath = location.pathname.replace(/^\/[a-z]{2}(-[A-Z]{2})?(?=\/|$)/, '') || '/'

  // Chat pages lock 100dvh; print pages flow naturally for unclipped multi-page printing
  const isChatPage = CHAT_PREFIXES.some(p => location.pathname.startsWith(p) || normalizedPath.startsWith(p))
  const isPrintPage = PRINT_PREFIXES.some(p => location.pathname.startsWith(p) || normalizedPath.startsWith(p))

  const { branding: facilityBranding } = (() => {
    try { return { branding: useFacilityBranding() } } catch { return { branding: null } }
  })()
  const isBrandedSubdomain = !!(facilityBranding?.is_branded)

  // Pages that ship their own navigation chrome — no shared Header/Footer
  const OWN_CHROME_PREFIXES = ['/elite', '/elite-academy', '/university', '/platform', '/whitelabel', '/university-brand/admin', '/homeschool/enrol', '/schools', '/transfer']
  const isAcademyHome = IS_ACADEMY && !isBrandedSubdomain && (normalizedPath === '/' || normalizedPath === '')
  const isBrandedHome = isBrandedSubdomain && (normalizedPath === '/' || normalizedPath === '')
  const isSponsorPublic = normalizedPath === '/sponsor' || normalizedPath === '/sponsor/give' || normalizedPath === '/sponsor/sent' || normalizedPath === '/sponsor/corporate'
  const hasOwnChrome = isAcademyHome || isBrandedHome || isSponsorPublic || OWN_CHROME_PREFIXES.some(p => location.pathname.startsWith(p) || normalizedPath.startsWith(p))
  const hideChrome = isChatPage || isPrintPage || hasOwnChrome

  const isResendingRef = useRef(false)
  const [showVerifyBanner, setShowVerifyBanner] = useState(false)
  const { user, userProfile } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    // Show banner if logged in but unconfirmed
    if (user && user.email_confirmed_at === null) {
      setShowVerifyBanner(true)
    } else {
      setShowVerifyBanner(false)
    }
  }, [user])

  // P1 #15 — Warmup ping: fire 5s after mount to pre-warm ai-education function
  // Uses a fire-and-forget OPTIONS request (returns instantly, no auth needed)
  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        const supabase = createClientComponentClient()
        const url = `${(supabase as any).supabaseUrl}/functions/v1/ai-education`
        fetch(url, { method: 'OPTIONS' }).catch(() => { /* non-fatal */ })
      } catch { /* non-fatal */ }
    }, 5000)
    return () => clearTimeout(timer)
  }, [])

  const handleResend = async () => {
    if (isResendingRef.current || !user?.email) return
    isResendingRef.current = true
    const supabase = createClientComponentClient()
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: user.email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/verify`,
        }
      })
      if (!error) {
        alert('Verification email resent! Please check your inbox.')
      }
    } finally {
      isResendingRef.current = false
    }
  }
  useEffect(() => {
    if (!user) return
    const supabase = createClientComponentClient()
    const ping = () => {
      supabase.functions.invoke('ai-education', { body: { warmup: true } }).catch(() => { })
      supabase.functions.invoke('ai-conversation', { body: { warmup: true } }).catch(() => { })
    }
    const t0 = setTimeout(ping, 2000)
    const id = setInterval(ping, 180_000)
    return () => { clearTimeout(t0); clearInterval(id) }
  }, [user])

  // Capture Affiliate Referral Code
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const ref = params.get('ref')
    if (ref) {
      localStorage.setItem('sage_affiliate_ref', ref)
      const newUrl = window.location.pathname + window.location.search.replace(/[\?&]ref=[^&]+/, '').replace(/^&/, '?')
      window.history.replaceState({}, '', newUrl)
    }
  }, [])

  // ── Native-like Position Memory ──
  usePositionMemory()

  return (
    <MemoryPreloaderProvider>
      {/*
              KEY FIX: The root div no longer forces h-screen / h-[100dvh].
              Chat pages set their own height internally (h-[100dvh] on their
              outermost div). Non-chat pages scroll naturally via min-h-[100dvh].
              
              The previous pattern of wrapping everything in a flex column with
              h-screen was causing the chat page's own flex layout to get squeezed
              inside the outer flex column, producing the "zoomed" appearance.
            */}
      <div style={{
        height: isChatPage ? '100dvh' : 'auto',
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        overflow: isChatPage ? 'hidden' : 'visible',
      }}>
        {showVerifyBanner && (
          <div className="bg-amber-500 text-white px-4 py-2 text-center text-sm font-bold flex items-center justify-center gap-4 z-[9999]">
            <span>Please verify your email address to access all features.</span>
            <button
              onClick={handleResend}
              className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded-lg transition-colors border border-white/30"
            >
              Resend Email
            </button>
          </div>
        )}
        {!hideChrome && <Header />}

        <main style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: isChatPage ? 'hidden' : 'visible',
        }}>
          <AuthRedirectGuard />
          <ScrollManager />

          <Routes>
            {/* ── University branch — declared FIRST so /university/* never loses
         to the /:lang/<page> pattern below. See: /university/dashboard
         was matching /:lang/dashboard with lang='university'. ─────────── */}
            <Route path="/university" element={<UniversityLandingPage />} />
            <Route path="/university/dashboard" element={<UniversityDashboardPage />} />
            <Route path="/university/enrol" element={<UniversityEnrolPage />} />
            <Route path="/university/degree/:classId" element={<UniversityDegreePage />} />
            <Route path="/university/learn/:classId" element={<UniversitySessionPage />} />
            <Route path="/university/module/:classId" element={<UniversityModulePage />} />
            <Route path="/university/progress" element={<UniversityProgressPage />} />
            <Route path="/university/certificates" element={<UniversityCertificatesPage />} />
            <Route path="/university/corporate" element={<UniversityCorporatePage />} />
            <Route path="/university/corporate/admin" element={<UniversityCorporateAdminPage />} />
            <Route path="/university/ngo/dashboard" element={<UniversityNgoDashboardPage />} />
            <Route path="/university/nfp" element={<UniversityNfpPage />} />
            <Route path="/university/gift" element={<UniversityGiftPage />} />
            <Route path="/university/give" element={<UniversityGivePage />} />
            <Route path="/university/redeem" element={<UniversityRedeemPage />} />
            <Route path="/university/activate" element={<UniversityActivatePage />} />

            {/* ── Legacy /dashboard safe router — never 404 or dead-end ──── */}
            <Route path="/dashboard" element={<DashboardRedirect />} />
            <Route path="/:lang/dashboard" element={<DashboardRedirect />} />

            {/* ── Shared-shell routes (Header + Footer visible) ───────────── */}
            {routeList.map(({ path, Component }) => (
              <React.Fragment key={path}>
                <Route path={`/:lang/${path}`} element={<><LanguageRouteHandler /><Component /></>} />
                <Route path={`/${path}`} element={<Component />} />
              </React.Fragment>
            ))}

            {/* ── Church session (full-screen, no header) ──────────────────── */}
            <Route path="/church/session/:classId" element={<ChurchSessionPage />} />
            <Route path="/:lang/church/session/:classId" element={<><LanguageRouteHandler /><ChurchSessionPage /></>} />

            {/* Catch-all — main domain → SWA home; org subdomain → branded front door */}
            <Route path="*" element={<RootPortalRouter />} />
          </Routes>
        </main>

        {/* ── Support widget — authenticated users only (renders null for anon) ── */}
        <SupportWidget
          plan={(userProfile?.preferences as any)?.billing?.plan || userProfile?.active_plan || undefined}
          facilityId={userProfile?.facility_id || undefined}
          isSessionPage={isChatPage}
        />

        <PWAUpdateHelper />
        {!hideChrome && <Footer isChatPage={false} />}
      </div>
    </MemoryPreloaderProvider>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Root
// ─────────────────────────────────────────────────────────────────────────────
function App() {
  return (
    <I18nProvider>
      <SubdomainBrandingProvider>
        <AuthProvider>
          <PasswordSetupGate />
          <AccessibilityProvider>
            <AppShell />
          </AccessibilityProvider>
        </AuthProvider>
      </SubdomainBrandingProvider>
    </I18nProvider>
  )
}

export default App